import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { getSchoolsForMap, getAcademies } from '@/api/schools';
import { getSchoolNetworks } from '@/api/school-networks';
import type { SchoolMapItem, Academie } from '@/types/school';
import type { SchoolNetwork } from '@/types/school-network';
import { ArrowLeft, ExternalLink, MapPin, Users } from 'lucide-react';

// Fix Leaflet default icon paths for bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const defaultIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const vipIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [30, 47],
  iconAnchor: [15, 47],
  popupAnchor: [1, -38],
  shadowSize: [41, 41],
  className: 'leaflet-marker-vip',
});

const greenMarkerSvg = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z" fill="#16a34a" stroke="#15803d" stroke-width="1"/><circle cx="12.5" cy="12.5" r="5" fill="white"/></svg>`);
const greenMarkerUrl = `data:image/svg+xml,${greenMarkerSvg}`;

const burgundyMarkerSvg = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z" fill="#7f1d1d" stroke="#5c1414" stroke-width="1"/><circle cx="12.5" cy="12.5" r="5" fill="white"/></svg>`);
const burgundyMarkerUrl = `data:image/svg+xml,${burgundyMarkerSvg}`;

const cancelledIcon = new L.Icon({
  iconUrl: burgundyMarkerUrl,
  iconRetinaUrl: burgundyMarkerUrl,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const recentProductionIcon = new L.Icon({
  iconUrl: greenMarkerUrl,
  iconRetinaUrl: greenMarkerUrl,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const recentProductionVipIcon = new L.Icon({
  iconUrl: greenMarkerUrl,
  iconRetinaUrl: greenMarkerUrl,
  shadowUrl: markerShadow,
  iconSize: [30, 47],
  iconAnchor: [15, 47],
  popupAnchor: [1, -38],
  shadowSize: [41, 41],
});

function getStatusBadgeVariant(status: string): 'success' | 'warning' | 'default' | 'destructive' {
  switch (status) {
    case 'production':
      return 'success';
    case 'demo_empty':
    case 'demo_datas':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    default:
      return 'default';
  }
}

/**
 * Ray casting algorithm for point-in-polygon test
 */
function pointInPolygon(lat: number, lng: number, polygon: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][1], yi = polygon[i][0];
    const xj = polygon[j][1], yj = polygon[j][0];
    const intersect = ((yi > lng) !== (yj > lng)) &&
      (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInFeature(lat: number, lng: number, feature: GeoJSON.Feature): boolean {
  const geometry = feature.geometry;
  if (geometry.type === 'Polygon') {
    return pointInPolygon(lat, lng, (geometry as GeoJSON.Polygon).coordinates[0]);
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry as GeoJSON.MultiPolygon).coordinates.some(
      (polygon) => pointInPolygon(lat, lng, polygon[0])
    );
  }
  return false;
}

// Component to fly to a position on the map
function FlyTo({ position, zoom }: { position: [number, number] | null; zoom?: number }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom ?? map.getZoom(), { duration: 0.5 });
    }
  }, [map, position, zoom]);
  return null;
}

const FRANCE_CENTER: [number, number] = [46.603354, 1.888334];
const FRANCE_ZOOM = 6;

export function SchoolsMapPage() {
  const [allSchools, setAllSchools] = useState<SchoolMapItem[]>([]);
  const [academies, setAcademies] = useState<Academie[]>([]);
  const [geojsonData, setGeojsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [selectedAcademie, setSelectedAcademie] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [selectedVersion, setSelectedVersion] = useState<string>('all');
  const [selectedNetwork, setSelectedNetwork] = useState<string>('all');
  const [networks, setNetworks] = useState<SchoolNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
  const geoJsonRef = useRef<L.GeoJSON | null>(null);
  const markersRef = useRef<Map<number, L.Marker>>(new Map());

  // Load academies list + networks
  useEffect(() => {
    getAcademies().then(setAcademies).catch(console.error);
    getSchoolNetworks().then(setNetworks).catch(console.error);
  }, []);

  // Load GeoJSON
  useEffect(() => {
    fetch('/data/academies.geojson')
      .then((res) => res.json())
      .then(setGeojsonData)
      .catch(console.error);
  }, []);

  // Load all schools (status + version + network filters go to API)
  useEffect(() => {
    async function fetchSchools() {
      setLoading(true);
      try {
        const params: { status?: string; version?: string; network?: string } = {};
        if (selectedStatus && selectedStatus !== 'all') {
          params.status = selectedStatus;
        }
        if (selectedVersion && selectedVersion !== 'all') {
          params.version = selectedVersion;
        }
        if (selectedNetwork && selectedNetwork !== 'all') {
          params.network = selectedNetwork;
        }
        const data = await getSchoolsForMap(params);
        setAllSchools(data);
      } catch (error) {
        console.error('Error fetching schools for map:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchSchools();
  }, [selectedStatus, selectedVersion, selectedNetwork]);

  // Filter schools client-side by selected academie (point-in-polygon)
  const schools = useMemo(() => {
    if (selectedAcademie === 'all' || !geojsonData) {
      return allSchools;
    }
    const feature = geojsonData.features.find(
      (f) => f.properties?.code_academie === selectedAcademie
    );
    if (!feature) return allSchools;
    return allSchools.filter((s) => pointInFeature(s.latitude, s.longitude, feature));
  }, [allSchools, selectedAcademie, geojsonData]);

  // Compute the 10 most recent production schools (by productionDate) —
  // dédupliqué par école, un point par établissement pouvant se répéter
  const recentProductionIds = useMemo(() => {
    const bySchool = new Map<number, SchoolMapItem>();
    allSchools
      .filter((s) => s.status === 'production' && s.productionDate)
      .forEach((s) => bySchool.set(s.id, s));
    const productionSchools = [...bySchool.values()]
      .sort((a, b) => (b.productionDate! > a.productionDate! ? 1 : -1))
      .slice(0, 10);
    return new Set(productionSchools.map((s) => s.id));
  }, [allSchools]);

  // Get selected academie name
  const selectedAcademieName = useMemo(() => {
    if (selectedAcademie === 'all') return null;
    const fromList = academies.find((a) => a.code === selectedAcademie);
    if (fromList) return fromList.name;
    const fromGeo = geojsonData?.features.find(
      (f) => f.properties?.code_academie === selectedAcademie
    );
    return fromGeo?.properties?.name ?? null;
  }, [selectedAcademie, academies, geojsonData]);

  const handleAcademieChange = useCallback((code: string) => {
    setSelectedAcademie((prev) => (prev === code ? 'all' : code));
  }, []);

  // Click on an establishment in the sidebar list
  const handleSchoolClick = useCallback((school: SchoolMapItem) => {
    setFlyTarget([school.latitude, school.longitude]);
    const marker = markersRef.current.get(school.establishmentId);
    if (marker) {
      setTimeout(() => marker.openPopup(), 600);
    }
  }, []);

  // GeoJSON style
  const geoJsonStyle = useMemo(() => {
    return (feature: GeoJSON.Feature | undefined) => {
      const code = feature?.properties?.code_academie;
      const isSelected = selectedAcademie !== 'all' && code === selectedAcademie;
      return {
        color: isSelected ? '#2563eb' : '#6b7280',
        weight: isSelected ? 3 : 1,
        fillColor: isSelected ? '#3b82f6' : '#9ca3af',
        fillOpacity: isSelected ? 0.2 : 0.05,
      };
    };
  }, [selectedAcademie]);

  const onEachFeature = useCallback((feature: GeoJSON.Feature, layer: L.Layer) => {
    const name = feature.properties?.name;
    const code = feature.properties?.code_academie;
    if (name) {
      (layer as L.Path).bindTooltip(name, { sticky: true });
    }
    (layer as L.Path).on({
      click: () => {
        handleAcademieChange(code || 'all');
      },
      mouseover: (e: L.LeafletMouseEvent) => {
        const target = e.target as L.Path;
        target.setStyle({ weight: 2, fillOpacity: 0.15 });
      },
      mouseout: (e: L.LeafletMouseEvent) => {
        if (geoJsonRef.current) {
          geoJsonRef.current.resetStyle(e.target as L.Path);
        }
      },
    });
  }, [handleAcademieChange]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/schools">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <MapPin className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Carte des écoles
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Chargement...' : `${schools.length} école${schools.length > 1 ? 's' : ''} sur ${allSchools.length}`}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Select
            value={selectedAcademie}
            onValueChange={setSelectedAcademie}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Toutes les academies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les academies</SelectItem>
              {academies.map((a) => (
                <SelectItem key={a.code} value={a.code}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedStatus}
            onValueChange={setSelectedStatus}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="active">Actives</SelectItem>
              <SelectItem value="production">En production</SelectItem>
              <SelectItem value="demo">Demos</SelectItem>
              <SelectItem value="created">En creation</SelectItem>
              <SelectItem value="cancelled">Resiliees</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={selectedNetwork}
            onValueChange={setSelectedNetwork}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Reseau" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les reseaux</SelectItem>
              {networks.map((n) => (
                <SelectItem key={n.id} value={String(n.id)}>
                  {n.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedVersion}
            onValueChange={setSelectedVersion}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Version" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes versions</SelectItem>
              <SelectItem value="v1">V1 uniquement</SelectItem>
              <SelectItem value="v2">V2 uniquement</SelectItem>
              <SelectItem value="both">V1 et V2</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Map + Sidebar */}
      <div className="flex gap-4" style={{ height: 'calc(100vh - 250px)' }}>
        {/* Map */}
        <div className="flex-1 rounded-lg border overflow-hidden relative">
          <MapContainer
            center={FRANCE_CENTER}
            zoom={FRANCE_ZOOM}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FlyTo position={flyTarget} zoom={14} />

            {geojsonData && (
              <GeoJSON
                key={selectedAcademie}
                ref={geoJsonRef}
                data={geojsonData}
                style={geoJsonStyle}
                onEachFeature={onEachFeature}
              />
            )}

            {schools.map((school) => {
              const isRecent = recentProductionIds.has(school.id);
              let icon = defaultIcon;
              if (isRecent && school.vip) icon = recentProductionVipIcon;
              else if (isRecent) icon = recentProductionIcon;
              else if (school.status === 'cancelled') icon = cancelledIcon;
              else if (school.vip) icon = vipIcon;
              return (
              <Marker
                key={school.establishmentId}
                position={[school.latitude, school.longitude]}
                icon={icon}
                ref={(ref) => {
                  if (ref) markersRef.current.set(school.establishmentId, ref);
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      {school.vip && <span className="inline-flex items-center rounded-sm bg-amber-100 px-1 py-0 text-[9px] font-bold text-amber-700 border border-amber-300">VIP</span>}
                      {isRecent && <span className="inline-flex items-center rounded-sm bg-emerald-100 px-1 py-0 text-[9px] font-bold text-emerald-700 border border-emerald-300">RÉCENT</span>}
                      <span className="font-semibold text-sm">{school.name}</span>
                    </div>
                    {!school.isPrincipal && (
                      <div className="text-xs text-gray-600 mb-1">
                        Établissement : {school.establishmentName}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mb-1 font-mono">{school.code}</div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={getStatusBadgeVariant(school.status)} className="text-xs">
                        {school.statusLabel}
                      </Badge>
                      {isRecent && school.productionDate && (
                        <span className="text-[10px] text-gray-400">
                          Prod. {new Date(school.productionDate).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/schools/${school.code}`}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Voir la fiche
                    </Link>
                  </div>
                </Popup>
              </Marker>
              );
            })}
          </MapContainer>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 z-[1000] rounded-lg border bg-white/95 backdrop-blur-sm px-3 py-2.5 shadow-md">
            <div className="text-[11px] font-semibold text-gray-700 mb-1.5">Légende</div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <svg width="14" height="20" viewBox="0 0 14 20" className="flex-shrink-0">
                  <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#3b82f6" />
                  <circle cx="7" cy="7" r="3" fill="white" />
                </svg>
                <span className="text-[11px] text-gray-600">École</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="20" viewBox="0 0 14 20" className="flex-shrink-0">
                  <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ca8a04" />
                  <circle cx="7" cy="7" r="3" fill="white" />
                </svg>
                <span className="text-[11px] text-gray-600">École VIP</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="20" viewBox="0 0 14 20" className="flex-shrink-0">
                  <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#7f1d1d" />
                  <circle cx="7" cy="7" r="3" fill="white" />
                </svg>
                <span className="text-[11px] text-gray-600">Résiliée</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="20" viewBox="0 0 14 20" className="flex-shrink-0">
                  <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#16a34a" />
                  <circle cx="7" cy="7" r="3" fill="white" />
                </svg>
                <span className="text-[11px] text-gray-600">10 dernières en production</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-[340px] flex-shrink-0 rounded-lg border bg-white flex flex-col overflow-hidden">
          {/* Sidebar header */}
          <div className="p-3 border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-sm">
                  {selectedAcademieName
                    ? `Academie de ${selectedAcademieName}`
                    : 'Toutes les écoles'}
                </span>
              </div>
              <span className="text-xs text-gray-500">{schools.length}</span>
            </div>
          </div>

          {/* School list */}
          <div className="flex-1 overflow-y-auto">
            {schools.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                Aucune école dans cette zone
              </div>
            ) : (
              schools.map((school) => {
                const isRecentSidebar = recentProductionIds.has(school.id);
                return (
                <button
                  key={school.establishmentId}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors ${isRecentSidebar ? 'bg-emerald-50/50' : ''}`}
                  onClick={() => handleSchoolClick(school)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {school.vip && <span className="inline-flex items-center rounded-sm bg-amber-100 px-1 py-0 text-[9px] font-bold text-amber-700 border border-amber-300 flex-shrink-0">VIP</span>}
                      {isRecentSidebar && <span className="inline-flex items-center rounded-sm bg-emerald-100 px-1 py-0 text-[9px] font-bold text-emerald-700 border border-emerald-300 flex-shrink-0">NEW</span>}
                      <span className="text-sm font-medium truncate">{school.name}</span>
                    </div>
                    <Badge variant={getStatusBadgeVariant(school.status)} className="text-[10px] flex-shrink-0 px-1.5 py-0">
                      {school.statusLabel}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-gray-400 font-mono mt-0.5 truncate">
                    {school.code}
                    {!school.isPrincipal && <span className="ml-1 font-sans">· {school.establishmentName}</span>}
                  </div>
                </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <style>{`
        .leaflet-marker-vip {
          filter: hue-rotate(200deg) saturate(1.5);
        }
        .leaflet-container {
          z-index: 0;
        }
      `}</style>
    </div>
  );
}
